<template>
    <!-- Figma "D6 · Add Service (Modal)": header band, "+ Add New Service", one block per service, Cancel / Save.
         Fields, validation and emits unchanged. -->
    <div id="serviceModal" ref="serviceModal" class="modal fade fxModal" data-bs-backdrop="static"
        data-bs-keyboard="false" tabindex="-1" aria-labelledby="exampleModalLabel" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered" style="max-width: 460px">
            <div class="modal-content">
                <div class="modal-body !p-[10px] font-roboto">
                    <FxModalHeader :title="isEditing ? 'Edit Service' : 'Add Service'" :dismiss="false"
                        @close="hide()" />

                    <div v-if="!isEditing" class="flex justify-end mt-[12px]">
                        <button type="button"
                            class="group flex items-center gap-[8px] h-[36px] pl-[3px] pr-[12px] rounded-full border-[1px] border-[#E9ECF1] bg-[#fff] text-[14px] text-[#333333] hover:text-[#fff] hover:bg-[#7695FF] [@media(max-width:1023px)]:!text-[#fff] [@media(max-width:1023px)]:!bg-[#7695FF] transition-all duration-300"
                            @click="newServiceClicked()">
                            <span class="w-[30px] h-[30px] rounded-full flex items-center justify-center bg-[#7695FF] group-hover:bg-[#fff] [@media(max-width:1023px)]:!bg-[#fff]">
                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" >
                                    <path d="M0 0h24v24H0z" fill="none" />
                                    <path fill="none" stroke="#fff" stroke-linecap="round" stroke-linejoin="round" class="group-hover:stroke-[#7695FF] [@media(max-width:1023px)]:!stroke-[#7695FF]"
                                        stroke-width="2"
                                        d="M15 19c0-2.21-2.686-4-6-4s-6 1.79-6 4m16-3v-3m0 0v-3m0 3h-3m3 0h3M9 12a4 4 0 1 1 0-8a4 4 0 0 1 0 8" />
                                </svg>
                            </span>
                            Add New Service
                        </button>
                    </div>

                    <!-- no own scroll: the modal body scrolls (a second scrollbar was showing) -->
                    <div class="mt-[12px] flex flex-col gap-[15px]">
                        <div v-for="(service, index) in serviceForms" :key="service.id"
                            class="flex flex-col gap-[12px] p-[12px] rounded-[10px] border-1 border-[#EFEFEF]">
                            <!-- Type -->
                            <div>
                                <div class="flex items-center justify-between mb-[8px]">
                                    <div class="relative flex items-center gap-2">
                                        <div class="w-[4px] h-[16px] rounded-[2px] bg-[#7C8CE8]"></div>
                                        <span class="text-[15px] font-medium text-[#2E3138]">Type</span>
                                    </div>

                                    <button v-if="serviceForms.length > 1" type="button" aria-label="Remove service"
                                        class="w-[26px] h-[26px] rounded-full border-0 bg-[#efefef]/40 flex items-center justify-center"
                                        @click="removeService(index)">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                                            viewBox="0 0 24 24">
                                            <path d="M0 0h24v24H0z" fill="none" />
                                            <path fill="#f67280" fill-rule="evenodd"
                                                d="M9.774 5L3.758 3.94l.174-.986a.5.5 0 0 1 .58-.405L18.411 5h.088h-.087l1.855.327a.5.5 0 0 1 .406.58l-.174.984l-2.09-.368l-.8 13.594A2 2 0 0 1 15.615 22H8.386a2 2 0 0 1-1.997-1.883L5.59 6.5h12.69zH5.5zM9 9l.5 9H11l-.4-9zm4.5 0l-.5 9h1.5l.5-9zm-2.646-7.871l3.94.694a.5.5 0 0 1 .405.58l-.174.984l-4.924-.868l.174-.985a.5.5 0 0 1 .58-.405z" />
                                        </svg>
                                    </button>
                                </div>
                                <div class="flex items-center gap-[20px]">
                                    <label
                                        v-for="opt in [{ value: 'recommended', label: 'Recommended', color: '#F5B301' }, { value: 'required', label: 'Required', color: '#EE5851' }]"
                                        :key="opt.value"
                                        class="flex items-center gap-[8px] m-0 cursor-pointer text-[14px] text-[#333333]">
                                        <input v-model="service.type" type="radio" :name="'type-' + service.id"
                                            :value="opt.value" class="hidden" @change="typeChanged(service)">
                                        <span
                                            class="w-[20px] h-[20px] rounded-full border-[2px] bg-[#fff] flex items-center justify-center"
                                            :style="{
                                                borderColor: opt.color,
                                                backgroundColor: service.type === opt.value ? opt.color : '#fff'
                                            }">
                                            <span v-if="service.type === opt.value"
                                                class="w-[8px] h-[8px] rounded-full bg-[#fff]"></span>
                                        </span>
                                        {{ opt.label }}
                                    </label>
                                </div>
                                <small v-if="service.errors.type" class="block mt-[4px] text-[12px] text-[#EA8A8A]">{{
                                    service.errors.type }}</small>
                            </div>

                            <!-- Component name -->
                            <label class="block m-0">
                                <span class="block mb-[5px] text-[14px] text-[#999999]">Component Name</span>
                                <input v-model="service.serviceName" type="text" placeholder="Enter Component Name"
                                    class="w-full h-[40px] px-[12px] rounded-[10px] border-1 bg-white outline-none text-[14px] text-[#333333] placeholder:text-[#A8AEB8] disabled:bg-[#F5F5F7] disabled:cursor-not-allowed"
                                    :class="service.errors.serviceName ? 'border-[#F67280] bg-[#FEF7F7]' : 'border-[#EFEFEF]'"
                                    @input="clearError(service, 'serviceName')">
                                <small v-if="service.errors.serviceName"
                                    class="block mt-[4px] text-[12px] text-[#EA8A8A]">{{ service.errors.serviceName
                                    }}</small>
                            </label>

                            <!-- Service (a div, not a label: the list inside a label would re-open the dropdown) -->
                            <div class="block m-0">
                                <span class="block mb-[5px] text-[14px] text-[#999999]">Service</span>
                                <FxSelect v-model="service.selectedService" :options="getBookingActions(service.type)"
                                    label-key="new_display_name" value-key="object" placeholder="Select Service"
                                    :disabled="!service.type" inline
                                    :button-class="service.errors.selectedService ? '!border-[#F67280] !bg-[#F3F4F7]' : ''"
                                    @change="serviceChanged(service)" />
                                <small v-if="service.errors.selectedService"
                                    class="block mt-[4px] text-[12px] text-[#EA8A8A]">{{ service.errors.selectedService
                                    }}</small>
                            </div>

                            <!-- Labor warranty + fee -->
                            <label :for="'laborWarranty-' + service.id"
                                class="flex items-center gap-[8px] m-0 cursor-pointer select-none text-[14px] font-semibold text-[#6C79E0]">
                                <input :id="'laborWarranty-' + service.id" v-model="service.laborWarranty"
                                    type="checkbox" class="sr-only" @change="laborWarrantyChanged(service)">
                                <span
                                    class="w-[20px] h-[20px] rounded-[3px] border-1 flex items-center justify-center"
                                    :class="service.laborWarranty ? 'border-[#6C79E0] bg-[#6C79E0]' : 'border-[#CCCCCC] bg-white'">
                                    <svg v-if="service.laborWarranty" width="14" height="14" viewBox="0 0 14 14"
                                        fill="none" aria-hidden="true">
                                        <path d="M3 7.4l2.6 2.5L11 4.4" stroke="#fff" stroke-width="1.6"
                                            stroke-linecap="round" stroke-linejoin="round" />
                                    </svg>
                                </span>
                                Part Warranty
                            </label>
                            <label class="block m-0">
                                <span class="block mb-[5px] text-[14px] text-[#999999]">Labor Fee</span>
                                <input v-model="service.laborFee" type="text" inputmode="decimal" placeholder="$0.00"
                                    :disabled="service.laborWarranty"
                                    class="w-full h-[40px] px-[12px] rounded-[10px] border-1 bg-white outline-none text-[14px] text-[#333333] placeholder:text-[#A8AEB8] disabled:bg-[#F5F5F7] disabled:cursor-not-allowed"
                                    :class="service.errors.laborFee ? 'border-[#F67280] bg-[#FEF7F7]' : 'border-[#EFEFEF]'"
                                    @input="laborFeeChanged(service)">
                                <small v-if="service.errors.laborFee"
                                    class="block mt-[4px] text-[12px] text-[#EA8A8A]">{{ service.errors.laborFee
                                    }}</small>
                            </label>
                        </div>
                    </div>

                    <!-- pinned to the bottom of the scrolling body -->
                    <div class="sticky bottom-0 z-[1] flex items-center justify-center gap-[10px] mt-[8px] py-[12px] bg-[#fff]">
                        <button type="button"
                            class="inline-flex items-center justify-center min-h-[38px] px-[26px] rounded-[5px] border-1 border-[#F67280] bg-[#fff] hover:bg-[#F67280] hover:text-[#fff] [@media(max-width:1023px)]:!bg-[#F67280] [@media(max-width:1023px)]:!text-[#fff] text-[14px] font-medium text-[#F67280] transition-all duration-300"
                            @click="hide()">
                            Cancel
                        </button>
                        <button type="button"
                            class="fxBtnPrimary bg-[#00B7C2] disabled:!bg-[#EFEFEF] disabled:!border-[#EFEFEF] disabled:text-[#999999] disabled:cursor-not-allowed transition-all duration-300"
                            :disabled="!isAllServicesValid" @click="submitClicked()">
                            Save
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from "vuex";
import { Modal } from "bootstrap";
import FxModalHeader from "./FxModalHeader.vue";
import FxSelect from "../partialComponent/FxSelect.vue";

let formId = 0;

function emptyServiceForm() {
    return {
        id: ++formId,
        type: '',
        serviceName: '',
        selectedService: '',
        laborWarranty: false,
        laborFee: '',
        errors: {}
    };
}

export default {
    components: {
        FxModalHeader,
        FxSelect,
    },

    data() {
        return {
            modal: null,
            serviceForms: [emptyServiceForm()],
            // Index of the service being edited on the summary page, null when adding
            editIndex: null
        };
    },

    emits: ["save"],

    computed: {
        ...mapGetters(["bookingServices"]),

        isEditing() {
            return this.editIndex !== null;
        },

        isAllServicesValid() {
            return this.serviceForms.every(service => {
                const typeValid = !!service.type;

                const serviceNameValid =
                    service.serviceName &&
                    service.serviceName.trim() !== '';

                const selectedServiceValid =
                    !!service.selectedService;

                // Either Labor Warranty OR Labor Fee
                const laborValid =
                    service.laborWarranty ||
                    (service.laborFee && service.laborFee.trim() !== '');

                return (
                    typeValid &&
                    serviceNameValid &&
                    selectedServiceValid &&
                    laborValid
                );
            });
        }
    },

    mounted() {
    },

    watch: {
    },

    methods: {

        show(service = null, index = null) {
            this.editIndex = service ? index : null;
            this.serviceForms = [service ? this.formFromService(service) : emptyServiceForm()];

            if (this.modal == null) {
                this.modal = new Modal(this.$refs.serviceModal);

                this.$refs.serviceModal.addEventListener("hidden.bs.modal", () => {
                    document.documentElement.style.overflow = "";
                });
            }

            this.modal.show();
        },

        hide() {
            if (this.modal == null) {
                return;
            }

            this.modal.hide();
            this.resetForms();
        },

        resetForms() {
            this.editIndex = null;
            this.serviceForms = [emptyServiceForm()];
        },

        formFromService(service) {
            const type = service.value == 3 ? 'required' : 'recommended';
            // Match against the current list so the select shows the saved service
            const selectedService = this.getBookingActions(type)
                .find(action => action.id == service.booking_action_id) ?? '';

            return {
                ...emptyServiceForm(),
                type,
                serviceName: service.option ?? '',
                selectedService,
                laborWarranty: !!service.is_service_warranty,
                laborFee: service.is_service_warranty || service.diagnosis_labor_price == null
                    ? ''
                    : String(service.diagnosis_labor_price),
            };
        },

        getBookingActions(type) {
            const actions = this.bookingServices?.actions;
            if (!actions || !type) {
                return [];
            }
            return actions[type] ?? [];
        },

        typeChanged(service) {
            this.clearError(service, 'type');
            // Services differ per type, so drop a selection from the other list
            service.selectedService = '';
        },

        serviceChanged(service) {
            this.clearError(service, 'selectedService');
            // Labor Fee defaults to the selected service's price
            if (!service.laborWarranty && service.selectedService?.price != null) {
                service.laborFee = String(service.selectedService.price);
                this.clearError(service, 'laborFee');
            }
        },

        clearError(service, field) {
            if (service.errors[field]) {
                service.errors[field] = '';
            }
        },

        laborWarrantyChanged(service) {
            // Warranty checked means Labor Fee requirement is satisfied
            if (service.laborWarranty) {
                service.laborFee = '';
                service.errors.laborFee = '';
            } else if (service.selectedService?.price != null) {
                service.laborFee = String(service.selectedService.price);
            }

            // If warranty is unchecked and there is no labor fee,
            // don't immediately show an error.
            // Error will be shown when Add New Service is clicked.
        },

        laborFeeChanged(service) {
            // Digits with a single dot and at most 2 decimals
            const [integer, ...rest] = service.laborFee.replace(/[^0-9.]/g, '').split('.');
            service.laborFee = rest.length ? `${integer}.${rest.join('').slice(0, 2)}` : integer;

            // If user starts entering Labor Fee,
            // remove Labor Fee error
            if (service.laborFee) {
                service.errors.laborFee = '';
            }
        },

        newServiceClicked() {
            // Only validate the FIRST card
            const service = this.serviceForms[0];

            service.errors = {};

            // Type
            if (!service.type) {
                service.errors.type = 'Please select type';
            }

            // Service Name
            if (!service.serviceName.trim()) {
                service.errors.serviceName = 'Service name is required';
            }

            // Select Service
            if (!service.selectedService) {
                service.errors.selectedService = 'Please select a service';
            }

            // Labor Warranty OR Labor Fee
            if (
                !service.laborWarranty &&
                !service.laborFee
            ) {
                service.errors.laborFee =
                    'Please select Labor Warranty or enter Labor Fee';
            }

            // Stop here if first card has errors
            if (Object.keys(service.errors).length > 0) {
                return;
            }

            // First card is valid -> add new card at TOP
            this.serviceForms.unshift(emptyServiceForm());
        },

        removeService(index) {
            this.serviceForms.splice(index, 1);
        },

        submitClicked() {
            if (!this.isAllServicesValid) {
                return;
            }

            // Same shape as the diagnostic options on the summary page
            const services = this.serviceForms.map(service => {
                const data = {
                    diagnostic_option_id: null,
                    option: service.serviceName.trim(),
                    value: service.type === 'required' ? 3 : 2,
                    selected_service: service.selectedService,
                    booking_action_id: service.selectedService.id,
                    is_service_warranty: service.laborWarranty,
                    parts: [],
                    is_error: false,
                    error: ''
                };
                // No labor price when the labor is under warranty
                if (!service.laborWarranty) {
                    data.diagnosis_labor_price = Number(service.laborFee);
                }
                return data;
            });

            this.$emit("save", services, this.editIndex);
            this.modal.hide();
            this.resetForms();
        },

    }
}
</script>